/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { pageTypes } from '@/constants/page';
import {
  getCategoryResultsFromCache,
  prefetchCategoryResults,
} from '@/features/category/api/getCategoryResults';
import { prefetchProductDataTopNavMenuV2 } from '@/features/topNav/api/getProductDataTopNavMenuV2';
import {
  getProductShelfResultsFromCache,
  prefetchProductShelfResultsInfinite,
} from '@/features/category/api/getProductShelfResults';
import {
  getProductShelfACESResultsFromCache,
  prefetchProductShelfACESResults,
} from '@/features/category/api/getProductShelfACESResults';
import { prefetchCategoryFAQ } from '@/features/category/api/getCategoryFAQ';
import { prefetchCategoryYears } from '@/features/category/api/getCategoryYears';
import { prefetchCMSCategoryData } from '@/features/category/api/getCMSCategoryData';
import { mapProductShelfData } from '@/features/category/api/mapProductShelfData';
import { mapACESProductShelfData } from '@/features/category/api/mapACESProductShelfData';
import { getPageTypeFromCache, prefetchPageType } from '@/features/category/api/getPageType';
import { getShopByPageName } from '@/features/category/utils/getShopByPageName';
import { getGlobalConfigFromCache, getFeatureFlag } from '@/features/globalConfig';
import { getHeaderDataFromCache } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { getPageDependentPrefooterParams } from '@/features/prefooter/utils/getPageDependentPrefooterParams';
import { prefetchProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { getACESShopByType, getShopByType } from '@/features/shelf/utils/getShopByType';
import { prefetchTopReviews } from '@/features/shelf/api/getTopReviews';
import { prefetchYearsTable } from '@/features/shelf/api/getYearsTable';
import { getSkuIdsForPage } from '@/features/shelf/utils/getSkuIdsForPage';
import { prefetchPreFooterData } from '@/features/prefooter/api/getPreFooterData';
import type { AppState } from '@/stores/interface';
import { ProductListProvider } from '@/features/shelf/context/ProductListProvider';
import { ProductListViewProvider } from '@/features/shelf/context/ProductListViewProvider';
import { isEmptyObject } from '@/utils/common';
import { configureSharedPageData } from '@/utils/configureSharedPageData.server';
import {
  getPathnameFromAsPath,
  parseUrl,
  sanitizeUrlWithParamList,
  stringifyUrl,
} from '@/utils/urlHelpers';
import React from 'react';
import { dehydrate } from '@tanstack/react-query';
import root from 'window-or-global';
import RoutingComponent from '../components/RoutingComponent';
import { countryCodes } from '@/constants/locale';
import { routePaths } from '../constants/routePaths';
import type { PageContextType } from '../next-types';
import Router from 'next/router'; // eslint-disable-line no-restricted-imports
import { FACETED_URL, FACETED_URL_V2 } from '@/utils/validatorRegex';
import logger from '@/utils/logger';
import { getDevice, getDeviceType } from '@/utils/getDeviceType.server';
import { prefetchDynamicLinkMappings } from '@/features/dlm/api/getDynamicLinkMappings';
import { cookieConstant } from '@/constants/cookies';
import environmentConfig from '@/config/environment';
import { acceptableShelfQueryParams } from '@/constants/queryParams';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { prefetchSeoMetadataInfoResults } from '@/features/common/api/getSeoMetadata';
import { getCookies } from '@/utils/cookie';
import { normalizeMakeName } from '@/utils/normalizeMakeName';
import { normalizeModelName } from '@/utils/normalizeModelName';
import { prefetchCitrusShelfData } from '@/features/citrus/api/getCitrusShelfData';
import { prefetchCitrusCategoryData } from '@/features/citrus/api/getCitrusCategorypageData';
import {
  getCitrusShelfCarouselDataFromCache,
  prefetchCitrusShelfCarouselData,
} from '@/features/citrus/api/getCitrusShelfCarouselData';
import {
  getPLPDataFromCache,
  prefetchContentStackProductListingPageData,
} from '@/features/productListingPage/api/getContentStackProductListingPageData';
import type { PageTypeData } from '@/types';
import { getAcesFlag } from '@/features/ymme/utils/acesFlags';
import { getExperimentId } from '@/utils/searchExperiment';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { prefetchCategoryByModel } from '@/features/category/api/getCategoryModels';
import { addVehicleFromQueryParams } from '@/features/sharableLink/utils/addVehicleFromQueryParams';
import {
  getCatalogConfigsFromCache,
  prefetchCatalogConfigs,
} from '@/features/common/api/getCatalogConfig';
import { type CatalogConfigsOptions } from '@/features/common';
import type {
  ProductShelfACESResultsModel,
  ProductShelfResultsModel,
} from '@/api/types/browse-search-types';
import type { ProductShelfACESResponse, ProductShelfResponse } from '@/features/category';
import { getOMSFlags } from '@/features/product/utils/getOMSFlags';
import { getGCPPriceFlags } from '@/features/product/utils/getGCPPriceFlags';
import { getVertexPersonalizationParams } from '@/features/search/hooks/useVertexPersonalizationParams';
import { prefetchBuyAgainPurchases } from '@/features/orderHistory/api/getBuyAgainPurchases';
import { getFormattedDateRange } from '@/features/orderHistory/utils/getFormattedDateRange';
import { isServer } from '@/utils/isServer';
import { getGlobalStateQueryData } from '@/hooks/useGlobalState';
import { prefetchSeoFaqs } from '@/features/common/api/getSeoFaqs';
import { getForteDecisionFlag } from '@/features/forte/api/getForteDecision';
import { getShelfRequestStoreId } from '@/utils/getShelfRequestStoreId';

// TODO: replace req.url with asPath when Next bug is fixed
// https://github.com/vercel/next.js/issues/46876

export const config = {
  unstable_JsPreload: false,
};

function RoutingPage() {
  return <RoutingComponent />;
}

const CATEGORY_PAGE_TYPES: unknown[] = [
  pageTypes.TopNavCategory,
  pageTypes.LeafCategory,
  pageTypes.CategoryGrouping,
];

RoutingPage.getInitialProps = async (context: PageContextType) => {
  const { query, res, req, asPath } = context;
  const { seoUrlPath: path } = query;
  if (!Array.isArray(path)) {
    throw new Error('Invalid seoUrl path');
  }

  if (path[0] === 'videos') {
    if (isServer) {
      return {
        redirect: {
          destination: routePaths.azVideos,
          permanent: false,
        },
      };
    } else {
      void Router.replace(routePaths.azVideos);
    }

    return {};
  }

  if (path[0] === 'company') {
    if (isServer) {
      res?.writeHead(302, { Location: req?.url }).end();
    } else {
      root.location.href = asPath;
    }
    return {};
  }

  if (path[0] === 'signin') {
    if (isServer) {
      res?.writeHead(302, { Location: routePaths.signIn }).end();
    } else {
      window.location.replace(routePaths.signIn);
    }
    return {};
  }

  if (isServer && req) {
    const parsedUrl = req.url ? parseUrl(req.url) : null;
    const isRequestForAsset = req.url && parsedUrl?.url.includes('.');
    const cleanedReqUrl = parsedUrl ? stringifyUrl(parsedUrl) : undefined;
    if (isRequestForAsset) {
      logger.info({
        message: 'Skipping asset request',
        meta: {
          url: req.url,
        },
      });
      res?.writeHead(404, 'Not Found').end();
      return {};
    }

    const { axios, queryClient, initialAppState } = await configureSharedPageData(context);
    const appData = getGlobalStateQueryData('appData', queryClient) ?? {
      userAuthenticated: false,
      deviceType: '',
    };
    const isBot = appData.deviceType === 'bot';

    const device = getDevice(context.req);
    const deviceType = getDeviceType(context.req);
    const locale = initialAppState.locale;

    const globalConfig = getGlobalConfigFromCache(queryClient);

    const headerData = getHeaderDataFromCache(queryClient);
    let pageTypeData: PageTypeData | null | undefined = null;
    const country = getCountryFromLocale(locale);

    const citrusEnabled = globalConfig?.IS_CITRUS_ENABLED === 'true';
    const citrusShelfSponsoredCardsEnabled =
      globalConfig?.IS_CITRUS_SHELF_SPONSORED_CARDS_ENABLED === 'true';
    const citrusShelfBannersEnabled = globalConfig?.IS_CITRUS_SHELF_BANNERS_ENABLED === 'true';
    const hasFilters =
      (req.url && FACETED_URL.test(req.url.split('?')[0])) ||
      (req.url && FACETED_URL_V2.test(req.url.split('?')[0])) ||
      !!query.filters ||
      !!query.facet;

    let categoryCMSPromise: Promise<void> | undefined;
    let seoMetadataPromise: Promise<void> | undefined;
    let seoFaqsPromise: Promise<void> | undefined;

    const { acesEnabled, acesTransitionPhase } = getAcesFlag(queryClient);

    const rawExperimentId = getForteDecisionFlag(queryClient, 'experimentId');
    const experimentId = getExperimentId(rawExperimentId, acesEnabled);

    const seoFaqsEnabled = getForteDecisionFlag(queryClient, 'seoFaqsEnabled');

    try {
      if (req.url && !parsedUrl?.url.includes('.')) {
        await prefetchPageType(queryClient, axios, {
          canonicalPath: req.url,
          country,
          gcpUrlEnabled: acesEnabled,
          experimentId,
        });
        pageTypeData = getPageTypeFromCache(queryClient, {
          canonicalPath: req.url,
          country,
          gcpUrlEnabled: acesEnabled,
          experimentId,
        });
        if (pageTypeData?.redirectLocation) {
          const { intcmp } = context.query;
          const { url, query: queryParams } = parseUrl(pageTypeData.redirectLocation);
          const sanitizedRedirectPath = sanitizeUrlWithParamList(
            {
              ...queryParams,
              intcmp,
            },
            acceptableShelfQueryParams,
            url,
          );

          res?.writeHead(301, { Location: sanitizedRedirectPath }).end();
          return {
            initialAppState,
            dehydratedState: dehydrate(queryClient),
          };
        }
      }
    } catch {
      res?.writeHead(301, { Location: '/errorPage' }).end();
      return {};
    }

    const botEnabledFacetPath = pageTypeData?.botEnabledFacetPath ?? undefined;

    const taxonomyPath = pageTypeData?.taxonomyPath ?? '';

    const brandtaxonomyPath = pageTypeData?.botEnabledFacetPath
      ? `${taxonomyPath}${pageTypeData.botEnabledFacetPath}`
      : taxonomyPath;
    const vehicleQuestions = extractVehicleQuestions(
      headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
      true,
    ) as string;
    const preferredVehicle = getPreferredVehicle(headerData);
    let citrusCategoryPromise;
    let plpCMSData;

    const vertexParams = getVertexPersonalizationParams({
      userAuthenticated: appData.userAuthenticated,
      headerData,
      visitorId: initialAppState.visitorId,
      acesEnabled,
      experimentId,
      isMyProfileReady: !!headerData,
    });

    try {
      if (pageTypeData?.pageType !== pageTypes.ProductShelf) {
        const citrusEnabled =
          globalConfig?.IS_CITRUS_ENABLED === 'true' &&
          globalConfig.IS_CITRUS_CATEGORY_BANNERS_ENABLED === 'true';
        if (citrusEnabled && !!pageTypeData?.catalogRepositoryName) {
          citrusCategoryPromise = prefetchCitrusCategoryData(
            queryClient,
            context,
            pageTypeData.catalogRepositoryName,
            initialAppState.eCookieId,
          );
        }

        const hasCatalogPath = Boolean(pageTypeData?.categoryRepositoryIdPath);
        const hasMakeModelYearPath = Boolean(pageTypeData?.makeModelYearPath);

        const shouldUseV2MenuData =
          acesEnabled && pageTypeData?.pageType === pageTypes.CategoryGrouping;

        await Promise.all([
          // Use this conditional pattern
          ...(shouldUseV2MenuData && (hasCatalogPath || hasMakeModelYearPath)
            ? [
                // v2 of category results prefetch
                prefetchProductDataTopNavMenuV2(
                  queryClient,
                  {
                    country: getCountryFromLocale(locale),
                    year: Number(preferredVehicle?.year) || undefined,
                    makeId: Number(preferredVehicle?.makeId) || undefined,
                    modelId: Number(preferredVehicle?.modelId) || undefined,
                    vehicleTypeId: Number(preferredVehicle?.vehicleTypeId) || undefined,
                    preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
                    categoryIdPath: pageTypeData?.categoryRepositoryIdPath,
                    storeId: Number(headerData?.storeNumber) || undefined,
                    makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
                    ...(vehicleQuestions && { vehicleQuestions }),
                    acesEnabled,
                    ...(experimentId && { experimentId }),
                    locale,
                  },
                  axios,
                ),
              ]
            : [
                // Original v1 of category results prefetch
                prefetchCategoryResults(axios, queryClient, {
                  taxonomyPath: pageTypeData?.taxonomyPath,
                  canonicalPath: pageTypeData?.canonicalPath,
                  makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
                  vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
                  locale,
                  storeId: headerData?.storeNumber,
                  pageType: pageTypeData?.pageType,
                  preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
                  recsPerPage: '24',
                }),
              ]),
          citrusCategoryPromise,
        ]);
      } else {
        await prefetchContentStackProductListingPageData({
          queryClient,
          taxonomyPath: brandtaxonomyPath,
          context,
          productListingPageType: 'Default',
          userSegments: headerData?.segments?.sort() ?? [],
        });

        plpCMSData = getPLPDataFromCache({
          queryClient,
          taxonomyPath: brandtaxonomyPath,
          productListingPageType: 'Default',
          userSegments: headerData?.segments?.sort() ?? [],
        });

        const parsedUrl = parseUrl(req.url || '');
        const preferredVehicle = getPreferredVehicle(headerData);
        const commonOptions = {
          rewardsId: headerData?.myAccountMap?.rewardsId ?? '',
          eCookieId: initialAppState.eCookieId,
          path: cleanedReqUrl,
          preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
          taxonomyPath: pageTypeData.taxonomyPath,
          canonicalPath: pageTypeData.canonicalPath,
          botEnabledFacetPath,
          makeModelYearPath: pageTypeData.makeModelYearPath || undefined,
          vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
          locale,
          recsPerPage: '24',
          storeId: getShelfRequestStoreId(headerData?.storeNumber, isBot, botEnabledFacetPath),
          pageType: pageTypeData.pageType,
          ...(acesEnabled && {
            makeId: preferredVehicle?.makeId,
            modelId: preferredVehicle?.modelId,
            vehicleTypeId: preferredVehicle?.vehicleTypeId,
            year: preferredVehicle?.year ? Number(preferredVehicle.year) : undefined,
          }),
          ignoreVehicleSpecificProductsCheck:
            typeof parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'string'
              ? parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'true'
              : undefined,
          ...(vehicleQuestions && { vehicleQuestions }),
          ...(acesEnabled && experimentId && { experimentId }),
          ...(vertexParams.shouldInjectParams && {
            visitorId: vertexParams.visitorId,
            userId: vertexParams.userId,
          }),
        };

        await Promise.all([
          acesEnabled
            ? prefetchProductShelfACESResults(axios, queryClient, {
                ...commonOptions,
                partGroupId: pageTypeData.catalogRepositoryId ?? '',
              })
            : prefetchProductShelfResultsInfinite(axios, queryClient, { ...commonOptions }),
        ]);
      }
    } catch {
      res?.writeHead(301, { Location: '/errorPage' }).end();
      return {};
    }

    if (pageTypeData?.pageType === pageTypes.CategoryGrouping) {
      categoryCMSPromise = prefetchCMSCategoryData(
        queryClient,
        pageTypeData.taxonomyPath ?? '',
        pageTypeData.subPageType ?? '',
        context,
        headerData?.segments?.sort() ?? [],
        {
          make: pageTypeData.make ?? null,
          model: pageTypeData.model ?? null,
          year: pageTypeData.year ?? null,
        },
      );
    }

    const shopByPageName = getShopByPageName(pageTypeData);

    const categoryResultsData = getCategoryResultsFromCache(queryClient, deviceType, {
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
      taxonomyPath: pageTypeData?.taxonomyPath,
      canonicalPath: pageTypeData?.canonicalPath,
      makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
      vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
      locale,
      recsPerPage: '24',
      storeId: headerData?.storeNumber,
      pageType: pageTypeData?.pageType,
    });

    const commonOptions = {
      rewardsId: headerData?.myAccountMap?.rewardsId ?? '',
      eCookieId: initialAppState.eCookieId,
      path: cleanedReqUrl,
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
      taxonomyPath: pageTypeData?.taxonomyPath,
      canonicalPath: pageTypeData?.canonicalPath,
      botEnabledFacetPath: pageTypeData?.botEnabledFacetPath || undefined,
      makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
      vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
      locale,
      recsPerPage: '24',
      storeId: getShelfRequestStoreId(
        headerData?.storeNumber,
        isBot,
        pageTypeData?.botEnabledFacetPath,
      ),
      pageType: pageTypeData?.pageType,
      ...(acesEnabled && {
        makeId: preferredVehicle?.makeId,
        modelId: preferredVehicle?.modelId,
        vehicleTypeId: preferredVehicle?.vehicleTypeId,
        year: preferredVehicle?.year ? Number(preferredVehicle.year) : undefined,
      }),
      ignoreVehicleSpecificProductsCheck:
        typeof parsedUrl?.query.ignoreVehicleSpecificProductsCheck === 'string'
          ? parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'true'
          : undefined,
      ...(vehicleQuestions && { vehicleQuestions }),
      ...(acesEnabled && experimentId && { experimentId }),
      ...(vertexParams.shouldInjectParams && {
        visitorId: vertexParams.visitorId,
        userId: vertexParams.userId,
      }),
    };

    const categoryShelfResultsData = acesEnabled
      ? getProductShelfACESResultsFromCache(queryClient, {
          ...commonOptions,
          partGroupId: pageTypeData?.catalogRepositoryId ?? '',
        })?.pages[0]
      : getProductShelfResultsFromCache(queryClient, { ...commonOptions })?.pages[0];

    const partsProSpecificShelfData = acesEnabled
      ? undefined
      : (categoryShelfResultsData as ProductShelfResponse);

    const acesSpecificShelfData = acesEnabled
      ? (categoryShelfResultsData as ProductShelfACESResponse)
      : undefined;
    const catalogVehicleId = preferredVehicle?.catalogVehicleId ?? '0';
    const { useOMSInventoryQohService } = getOMSFlags(queryClient);
    const { useGCPPriceService } = getGCPPriceFlags(queryClient);
    const skuDetailsPromise =
      categoryShelfResultsData?.productShelfResults?.skuRecords &&
      categoryShelfResultsData.productShelfResults.skuRecords.length > 0
        ? prefetchProductSkuDetails(axios, queryClient, {
            skuIds: categoryShelfResultsData.productShelfResults.skuRecords.map(
              (skuRecord) => skuRecord.itemId,
            ),
            storeNumber: headerData?.storeNumber ?? '',
            vehicleId: catalogVehicleId,
            isNewDealsApiEnabled:
              getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true',
            useOMSInventoryQohService,
            useGCPPriceService,
            acesEnabled,
          })
        : undefined;

    const mappedCategorydata = acesEnabled
      ? acesSpecificShelfData && mapACESProductShelfData(acesSpecificShelfData, pageTypeData)
      : partsProSpecificShelfData && mapProductShelfData(partsProSpecificShelfData, pageTypeData);

    const buyAgainContainerBlock = plpCMSData?.shelfpagebody?.find(
      (block) =>
        'buy_again_container' in block && !!block.buy_again_container?.buy_again_container?.[0],
    );
    const buyAgainConfig = buyAgainContainerBlock?.buy_again_container?.buy_again_container?.[0];
    const buyAgainEndpointEnabled =
      getFeatureFlag(globalConfig, 'BUY_AGAIN_ENDPOINT_ENABLED') === 'true';
    const dateRange = Number(getFeatureFlag(globalConfig, 'BUY_AGAIN_DATE_RANGE'));

    if (appData.userAuthenticated && buyAgainConfig && buyAgainEndpointEnabled) {
      const { fromDate, toDate } = getFormattedDateRange({
        date: new Date(),
        dateRange,
      });

      await prefetchBuyAgainPurchases(
        queryClient,
        {
          fromDate,
          toDate,
        },
        axios,
      );
    }

    const redirectPath =
      partsProSpecificShelfData?.redirectLocation ||
      acesSpecificShelfData?.redirectUrl ||
      categoryResultsData?.redirectLocation ||
      undefined;

    if (pageTypeData?.pageType === undefined) {
      if (req.url?.includes('.json')) {
        return {
          dehydratedState: dehydrate(queryClient),
          initialAppState,
        };
      }
      if (!res?.headersSent) {
        res?.writeHead(301, { Location: '/errorPage' }).end();
      }
    } else if (redirectPath && !req.url?.startsWith('/_next/')) {
      const parsedUrl = parseUrl(redirectPath);

      let sanitizedPath = redirectPath;

      if (!isEmptyObject(parsedUrl.query)) {
        sanitizedPath = parsedUrl.url;
      }

      // Do not send full path with query params for bots
      // We explicitly append recsPerPage: 24 for bots when constructing the URL when making the query in prefetchCategory.
      // However, even though we are requesting 24 recsPerPage,
      // SEO does not want this query param to show up in the redirect url since bots are technically not be able to change recsPerPage.
      const path = isBot ? sanitizedPath : redirectPath;
      if (!res?.headersSent) {
        res?.writeHead(301, { Location: path }).end();
      }
    }

    let dynamicLinksPromise: Promise<void> | undefined;
    let categoryFAQPromise: Promise<void> | undefined;
    let yearsTablePromise: Promise<void> | undefined;
    let categoryYearsPromise: Promise<void> | undefined;
    let citrusSkuDetailsPromise: Promise<void> | undefined;
    let topReviewsPromise: Promise<void> | undefined;

    const catalogConfigItemId = acesEnabled
      ? (acesSpecificShelfData?.productShelfResults?.partGroupId ??
        acesSpecificShelfData?.productShelfResults?.partTerminologyId)
      : mappedCategorydata?.productId;

    const catalogConfigsOptions: CatalogConfigsOptions = {
      itemId: catalogConfigItemId ?? '',
      itemType: 'product',
      country,
    };
    const catalogConfigsPromise = catalogConfigItemId
      ? prefetchCatalogConfigs(catalogConfigsOptions, queryClient, axios)
      : undefined;

    if (pageTypeData?.pageType === pageTypes.ProductShelf && req.url) {
      if (
        citrusEnabled &&
        citrusShelfSponsoredCardsEnabled &&
        !hasFilters &&
        pageTypeData.catalogRepositoryName &&
        mappedCategorydata &&
        deviceType !== 'bot'
      ) {
        let shouldFetchCitrusData = false;
        const isVehicleSelected = !!headerData?.vehicleMap?.vehicleId;

        await catalogConfigsPromise;
        const catalogConfigsData = getCatalogConfigsFromCache(
          {
            itemId: catalogConfigsOptions.itemId,
            itemType: 'product',
            country: country,
          },
          queryClient,
        );
        shouldFetchCitrusData =
          !catalogConfigsData?.configs?.IS_SPNSRD_ADVRTSMT_FL || !isVehicleSelected;

        if (shouldFetchCitrusData) {
          citrusSkuDetailsPromise = (async () => {
            const citrusShelfOptions = {
              category: pageTypeData.catalogRepositoryName ?? '',
              vehicleId: catalogVehicleId,
            };

            await prefetchCitrusShelfCarouselData(
              queryClient,
              context,
              citrusShelfOptions,
              initialAppState.eCookieId,
            );

            const citrusData = getCitrusShelfCarouselDataFromCache(citrusShelfOptions, queryClient);

            const { sponsoredSkus } = getSkuIdsForPage(
              mappedCategorydata,
              req.url ?? '',
              citrusData,
            );

            if (sponsoredSkus.length > 0) {
              await prefetchProductSkuDetails(axios, queryClient, {
                skuIds: sponsoredSkus,
                storeNumber: headerData?.storeNumber ?? '',
                vehicleId: catalogVehicleId,
                isNewDealsApiEnabled:
                  getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true',
                useOMSInventoryQohService,
                useGCPPriceService,
                acesEnabled,
              });
            }
          })();
        }
      }

      if (
        pageTypeData.catalogRepositoryId &&
        device.type === 'bot' &&
        !shopByPageName &&
        getFeatureFlag(globalConfig, 'DLM_SHELF_ENABLED') === 'true' &&
        catalogVehicleId === '0'
      ) {
        dynamicLinksPromise = prefetchDynamicLinkMappings(
          {
            type: 'generic',
            country: getCountryFromLocale(locale),
            seoRepositoryId: pageTypeData.catalogRepositoryId,
          },
          queryClient,
          axios,
        );
      }
      if (
        pageTypeData.catalogRepositoryId &&
        device.type === 'bot' &&
        !!pageTypeData.make &&
        getFeatureFlag(globalConfig, 'DLM_YMM_SHELF_ENABLED') === 'true' &&
        catalogVehicleId === '0'
      ) {
        dynamicLinksPromise = prefetchDynamicLinkMappings(
          {
            type: 'vehicle',
            country: getCountryFromLocale(locale),
            seoRepositoryId: pageTypeData.catalogRepositoryId,
            vehicleOptions: {
              make: normalizeMakeName(pageTypeData.make),
              model: pageTypeData.model ? normalizeModelName(pageTypeData.model) : undefined,
              year: pageTypeData.year ?? undefined,
            },
          },
          queryClient,
          axios,
        );
      }

      if (
        getFeatureFlag(globalConfig, 'YEARS_TABLE_ENABLED') === 'true' &&
        shopByPageName === 'ShopByModel' &&
        partsProSpecificShelfData?.productShelfResults?.productId &&
        partsProSpecificShelfData.productShelfResults.make &&
        partsProSpecificShelfData.productShelfResults.model &&
        device.type === 'bot'
      ) {
        await catalogConfigsPromise;
        const catalogConfigsData = getCatalogConfigsFromCache(catalogConfigsOptions, queryClient);

        if (catalogConfigsData?.configs?.yearsTableEnabled) {
          yearsTablePromise = prefetchYearsTable(
            queryClient,
            {
              productId: partsProSpecificShelfData.productShelfResults.productId,
              make: partsProSpecificShelfData.productShelfResults.make,
              model: partsProSpecificShelfData.productShelfResults.model,
              hasAssociatedPartType:
                partsProSpecificShelfData.productShelfResults.hasAssociatedPartType,
              customerType: 'B2C',
              country: getCountryFromLocale(locale),
            },
            axios,
          );
        }
      }
    }

    if (
      deviceType === 'bot' &&
      pageTypeData?.pageType === pageTypes.ProductShelf &&
      categoryShelfResultsData?.productShelfResults &&
      req.url &&
      pageTypeData.canonicalPath
    ) {
      const shopByType = acesEnabled
        ? getACESShopByType(
            categoryShelfResultsData.productShelfResults as ProductShelfACESResultsModel,
          )
        : getShopByType(categoryShelfResultsData.productShelfResults as ProductShelfResultsModel);

      const categoryYearsMakeId = acesSpecificShelfData?.productShelfResults?.makeId;
      const categoryYearsModelId = acesSpecificShelfData?.productShelfResults?.modelId;
      const categoryYearsPartGroupId = acesSpecificShelfData?.productShelfResults?.partGroupId;

      if (
        categoryYearsMakeId &&
        categoryYearsModelId &&
        categoryYearsPartGroupId &&
        (shopByType === 'ShopByModel' || shopByType === 'ShopByYear')
      ) {
        categoryYearsPromise = prefetchCategoryYears(
          {
            locale,
            year: categoryShelfResultsData.productShelfResults.year,
            preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
            makeId: categoryYearsMakeId,
            modelId: categoryYearsModelId,
            partGroupId: categoryYearsPartGroupId,
            ...(experimentId && { experimentId }),
            ...(vertexParams.shouldInjectParams && {
              visitorId: vertexParams.visitorId,
              userId: vertexParams.userId,
            }),
          },
          queryClient,
          axios,
        );
      }
    }

    await Promise.all([
      skuDetailsPromise,
      citrusSkuDetailsPromise,
      yearsTablePromise,
      prefetchPreFooterData(
        queryClient,
        {
          locale,
          seourl: getPathnameFromAsPath(req.url ?? '').replace(/(\/bn\/|\/b\/).*/, ''),
          shouldCallACES: acesEnabled,
          experimentId,
          vehicleOptions: acesEnabled
            ? {
                vehicleId: undefined,
                vehicleTypeId: preferredVehicle?.vehicleTypeId,
                makeId: preferredVehicle?.makeId,
                modelId: preferredVehicle?.modelId,
                year: preferredVehicle?.year,
                vehicleQuestions: preferredVehicle?.vehicleQuestionsIds,
              }
            : {
                vehicleId: catalogVehicleId === '' ? '0' : catalogVehicleId,
                vehicleTypeId: undefined,
                makeId: undefined,
                modelId: undefined,
                year: undefined,
                vehicleQuestions: undefined,
              },
          ...getPageDependentPrefooterParams({
            isPDP: false,
            pageTypeData: pageTypeData ?? undefined,
            productDetails: undefined,
            acesEnabled,
          }),
        },
        axios,
      ),
      citrusEnabled &&
      citrusShelfBannersEnabled &&
      pageTypeData?.pageType === pageTypes.ProductShelf &&
      !!pageTypeData.catalogRepositoryName
        ? prefetchCitrusShelfData(
            queryClient,
            context,
            pageTypeData.catalogRepositoryName,
            initialAppState.eCookieId,
          )
        : undefined,
      catalogConfigsPromise,
      categoryYearsPromise,
    ]);

    if (query.vehicleId || query.year) {
      await addVehicleFromQueryParams(
        queryClient,
        query,
        locale,
        axios,
        asPath,
        { acesEnabled, acesTransitionPhase },
        {
          pageName: 'SEOUrlPath',
        },
      );
    }

    if (
      globalConfig &&
      pageTypeData &&
      (shopByPageName == 'ShopByMake' ||
        shopByPageName == 'ShopByModel' ||
        shopByPageName == 'ShopByYear') &&
      locale !== countryCodes.mx &&
      deviceType === 'bot' &&
      mappedCategorydata?.productId
    ) {
      topReviewsPromise = prefetchTopReviews(
        axios,
        queryClient,
        globalConfig,
        {
          minimumReviewRating: undefined,
          partTypeId: mappedCategorydata.productId,
          pageType: shopByPageName,
          vehicleMake: pageTypeData.make ?? undefined,
          vehicleModel: pageTypeData.model ?? undefined,
          vehicleYear: pageTypeData.year ?? undefined,
        },
        locale,
      );
    }

    const isPartsPage = pageTypeData?.catalogId === '0-0';
    const acesCategoryByModelConditions = acesEnabled
      ? Boolean(pageTypeData?.makeId && (pageTypeData.categoryRepositoryIdPath || isPartsPage))
      : true;

    if (
      req.url &&
      Boolean(pageTypeData?.make) &&
      !pageTypeData?.model &&
      pageTypeData?.pageType === pageTypes.CategoryGrouping &&
      acesCategoryByModelConditions
    ) {
      await prefetchCategoryByModel(
        {
          preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
          locale,
          seoUrl: req.url,
          shouldCallACES: acesEnabled,
          acesOptions: {
            makeId: pageTypeData.makeId ? Number(pageTypeData.makeId) : undefined,
            isPartsPage,
            categoryIdPath: pageTypeData.categoryRepositoryIdPath,
          },
          ...(experimentId && { experimentId }),
          ...(vertexParams.shouldInjectParams && {
            visitorId: vertexParams.visitorId,
            userId: vertexParams.userId,
          }),
        },
        queryClient,
        axios,
      );
    }

    if (CATEGORY_PAGE_TYPES.includes(pageTypeData?.pageType)) {
      if (deviceType === 'bot' && locale === countryCodes.us) {
        if (pageTypeData?.catalogRepositoryId) {
          if (
            !shopByPageName &&
            getFeatureFlag(globalConfig, 'DLM_CATEGORY_ENABLED') === 'true' &&
            catalogVehicleId === '0'
          ) {
            dynamicLinksPromise = prefetchDynamicLinkMappings(
              {
                type: 'generic',
                country: getCountryFromLocale(locale),
                seoRepositoryId: pageTypeData.catalogRepositoryId,
              },
              queryClient,
              axios,
            );
          }
          if (
            pageTypeData.makeModelYearPath &&
            pageTypeData.make &&
            getFeatureFlag(globalConfig, 'DLM_YMM_CATEGORY_ENABLED') === 'true' &&
            catalogVehicleId === '0'
          ) {
            dynamicLinksPromise = prefetchDynamicLinkMappings(
              {
                type: 'vehicle',
                country: getCountryFromLocale(locale),
                seoRepositoryId: pageTypeData.catalogRepositoryId,
                vehicleOptions: {
                  make: normalizeMakeName(pageTypeData.make),
                  model: pageTypeData.model ? normalizeModelName(pageTypeData.model) : undefined,
                  year: pageTypeData.year ?? undefined,
                },
              },
              queryClient,
              axios,
            );
          }

          categoryFAQPromise = prefetchCategoryFAQ(
            axios,
            locale,
            queryClient,
            {
              seoRepositoryType: 'CATEGORY',
              categoryId: pageTypeData.catalogRepositoryId,
            },
            globalConfig,
          );
        }
      }
    }

    if (
      (pageTypeData?.pageType === pageTypes.ProductShelf ||
        CATEGORY_PAGE_TYPES.includes(pageTypeData?.pageType)) &&
      !!pageTypeData?.canonicalPath
    ) {
      const selectedFacetValue =
        categoryShelfResultsData?.productShelfResults &&
        'selectedFacetValueDisplayName' in categoryShelfResultsData.productShelfResults
          ? categoryShelfResultsData.productShelfResults.selectedFacetValueDisplayName
          : undefined;

      seoMetadataPromise = prefetchSeoMetadataInfoResults(axios, queryClient, {
        vehicleMake: pageTypeData.make ?? undefined,
        vehicleModel: pageTypeData.model ?? undefined,
        vehicleYear: pageTypeData.year ?? undefined,
        seoUrl: pageTypeData.canonicalPath,
        botEnabledFacetPath,
        country: getCountryFromLocale(locale),
        customerType: 'B2C',
        preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
        pageType: pageTypeData.subPageType,
        firstFacetValue: selectedFacetValue || undefined,
        makeModelYearPath: pageTypeData.makeModelYearPath,
        shouldCallACES: acesEnabled,
      });
    }

    const faqsURL = getPathnameFromAsPath(asPath);

    if (
      pageTypeData?.pageType === pageTypes.ProductShelf &&
      deviceType === 'bot' &&
      seoFaqsEnabled
    ) {
      seoFaqsPromise = prefetchSeoFaqs(
        {
          locale,
          url: faqsURL,
        },
        queryClient,
        axios,
      );
    }

    await Promise.all([
      categoryCMSPromise,
      seoMetadataPromise,
      categoryFAQPromise,
      dynamicLinksPromise,
      topReviewsPromise,
      seoFaqsPromise,
    ]);

    let isListView =
      globalConfig?.IS_DEFAULT_GRID_VIEW_ENABLED === 'true' ? false : locale !== countryCodes.mx;
    const cookies = await getCookies({ req, res });
    const shelfListGridViewCookie = cookies[cookieConstant.names.shelfListGridView];

    let filterOpenStates = '{}';
    const filterOpenStatesCookie = cookies[cookieConstant.names.filterOpenStates];

    if (shelfListGridViewCookie) {
      isListView = shelfListGridViewCookie !== cookieConstant.values.shelfListGridView;
    }

    if (filterOpenStatesCookie) {
      filterOpenStates = filterOpenStatesCookie;
    }

    return {
      dehydratedState: dehydrate(queryClient),
      initialAppState,
      isListView,
      filterOpenStates,
    };
  }

  return {};
};

RoutingPage.getLayout = (
  page: React.ReactElement,
  pageProps: { isListView?: boolean; filterOpenStates?: string; initialAppState: AppState },
) => {
  let isListView = true;

  if (
    'initialAppState' in pageProps &&
    'IS_DEFAULT_GRID_VIEW_ENABLED' in pageProps.initialAppState
  ) {
    isListView = !pageProps.initialAppState.IS_DEFAULT_GRID_VIEW_ENABLED;
  }

  if ('isListView' in pageProps && pageProps.isListView != null) {
    isListView = pageProps.isListView;
  }

  let filterOpenStates: string | undefined = '{}';
  if ('filterOpenStates' in pageProps) {
    filterOpenStates = pageProps.filterOpenStates;
  }

  if ('initialAppState' in pageProps && pageProps.initialAppState.locale === 'es-MX') {
    isListView = false;
  }

  if ('initialAppState' in pageProps && pageProps.initialAppState.locale === countryCodes.ptBr) {
    isListView = false;
  }

  return (
    <ProductListProvider>
      <ProductListViewProvider isListView={isListView} filterOpenStates={filterOpenStates}>
        {page}
      </ProductListViewProvider>
    </ProductListProvider>
  );
};

export default RoutingPage;
